import { useEffect, useMemo, useRef, useState } from 'react';

import { AlgoliaService } from '@playbooks/services';
import { Button } from '@playbooks/ui/components/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@playbooks/ui/components/dialog';
import { Icon } from '@playbooks/ui/components/icon';
import { Input } from '@playbooks/ui/components/input';

import { useSearch } from 'contexts';

type SearchModalProps = {
	tableName: string;
	placeholder: string;
	open: boolean;
	setOpen: (open: boolean) => void;
};

const SearchModal = ({ tableName, placeholder, open, setOpen }: SearchModalProps) => {
	const [query, setQuery] = useState('');
	const [results, setResults] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const search = useSearch();
	const client = useMemo(() => new AlgoliaService({ tableName }), [tableName]);

	useEffect(() => {
		if (!open) {
			setQuery('');
			setResults([]);
			return;
		}
		window.setTimeout(() => inputRef.current?.focus(), 100);
	}, [open]);

	useEffect(() => {
		if (query.trim().length < 3) {
			setResults([]);
			return;
		}

		let cancelled = false;
		const timeout = window.setTimeout(async () => {
			setLoading(true);
			try {
				const response = await client.queryIndex(query, { page: 0, pageSize: 8 });
				if (!cancelled) setResults(response.data);
			} finally {
				if (!cancelled) setLoading(false);
			}
		}, 200);

		return () => {
			cancelled = true;
			window.clearTimeout(timeout);
		};
	}, [client, query]);

	const onSelect = result => {
		setOpen(false);
		search.onClick(result);
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogContent showCloseButton={false} className='top-[18vh] max-w-xl translate-y-0 gap-0 overflow-hidden p-0'>
				<DialogTitle className='sr-only'>Search documentation</DialogTitle>
				<DialogDescription className='sr-only'>Search Playbooks documentation by title and content.</DialogDescription>
				<div className='relative border-b p-3'>
					<Icon
						type='far'
						icon='magnifying-glass'
						className='pointer-events-none absolute top-1/2 left-6 -translate-y-1/2 text-foreground-body'
						aria-hidden
					/>
					<Input
						ref={inputRef}
						size='lg'
						value={query}
						onChange={event => setQuery(event.target.value)}
						placeholder={placeholder}
						className='border-0 bg-transparent pr-14 pl-10 shadow-none focus-visible:ring-0'
					/>
					<kbd className='absolute top-1/2 right-6 -translate-y-1/2 rounded-control border bg-surface px-1.5 py-0.5 text-[10px] font-medium'>
						ESC
					</kbd>
				</div>
				<div className='custom-scrollbar max-h-[min(28rem,60vh)] overflow-y-auto p-2'>
					{loading ? (
						<p className='px-3 py-8 text-center text-sm text-foreground-body'>Searching…</p>
					) : query.trim().length < 3 ? (
						<p className='px-3 py-8 text-center text-sm text-foreground-body'>Enter at least three characters.</p>
					) : results.length ? (
						<ul className='space-y-1'>
							{results.map(result => (
								<li key={result.objectID || result.id || `${result.category}-${result.uuid}`}>
									<Button
										variant='ghost'
										onClick={() => onSelect(result)}
										className='h-auto w-full justify-start whitespace-normal px-3 py-3 text-left'>
										<span className='min-w-0'>
											<span className='block text-sm font-medium'>{result.title || result.name}</span>
											{result.description && (
												<span className='mt-1 line-clamp-2 block text-xs leading-5 text-foreground-body'>
													{result.description}
												</span>
											)}
										</span>
									</Button>
								</li>
							))}
						</ul>
					) : (
						<p className='px-3 py-8 text-center text-sm text-foreground-body'>No documentation found.</p>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export { SearchModal };
